import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { SiteContent, defaultSiteContent, mergeSiteContent } from './siteContent';

declare global {
  interface Window {
    __GAMESIDE_SITE_CONTENT__?: SiteContent;
  }
}

type SiteContentContextValue = {
  content: SiteContent;
  isLoaded: boolean;
  refresh: () => Promise<void>;
};

function getInitialContent() {
  if (typeof window === 'undefined' || !window.__GAMESIDE_SITE_CONTENT__) {
    return defaultSiteContent;
  }

  return mergeSiteContent(window.__GAMESIDE_SITE_CONTENT__);
}

const SiteContentContext = createContext<SiteContentContextValue>({
  content: getInitialContent(),
  isLoaded: false,
  refresh: async () => {},
});

const supabaseContentTable = import.meta.env.VITE_SUPABASE_CONTENT_TABLE || 'site_content';
const supabaseContentRowId = import.meta.env.VITE_SUPABASE_CONTENT_ROW_ID || 'main';

async function loadSupabaseSiteContent() {
  const { isSupabaseBrowserConfigured, supabaseBrowserClient } = await import('../lib/supabaseClient');

  if (!isSupabaseBrowserConfigured || !supabaseBrowserClient) {
    return null;
  }

  const { data, error } = await supabaseBrowserClient
    .from(supabaseContentTable)
    .select('content')
    .eq('id', supabaseContentRowId)
    .maybeSingle();

  if (error || !data?.content) {
    if (error) {
      console.error('Unable to load Supabase site content:', error.message);
    }

    return null;
  }

  return mergeSiteContent(data.content as Partial<SiteContent>);
}

export function SiteContentProvider({ children }: { children: React.ReactNode }) {
  const [content, setContent] = useState<SiteContent>(() => getInitialContent());
  const [isLoaded, setIsLoaded] = useState(() => typeof window !== 'undefined' && Boolean(window.__GAMESIDE_SITE_CONTENT__));

  const refresh = async () => {
    try {
      const supabaseContent = await loadSupabaseSiteContent();

      if (supabaseContent) {
        setContent(supabaseContent);
        return;
      }

      const response = await fetch('/api/site-content', {
        credentials: 'same-origin',
        cache: 'no-store',
        headers: {
          Accept: 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`Unable to load site content: ${response.status}`);
      }

      const contentType = response.headers.get('content-type') || '';
      if (!contentType.includes('application/json')) {
        throw new Error('Site content endpoint did not return JSON.');
      }

      const payload = (await response.json()) as Partial<SiteContent>;
      setContent(mergeSiteContent(payload));
    } catch (error) {
      console.error(error);
      const supabaseContent = await loadSupabaseSiteContent();
      setContent(supabaseContent || getInitialContent());
    } finally {
      setIsLoaded(true);
    }
  };

  useEffect(() => {
    void refresh();
  }, []);

  const value = useMemo(
    () => ({
      content,
      isLoaded,
      refresh,
    }),
    [content, isLoaded],
  );

  return <SiteContentContext.Provider value={value}>{children}</SiteContentContext.Provider>;
}

export function useSiteContent() {
  return useContext(SiteContentContext);
}
